/**
 * CalendarStatusBadge.js
 * Enterprise-level calendar provider connection status badges.
 * Shows Google/Outlook status with branded colors, animations, and rich tooltips.
 */
import React from 'react';
import { Box, Tooltip, Typography, Skeleton } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';

const GOOGLE_ICON = 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg';
const OUTLOOK_ICON = "data:image/svg+xml;charset=utf-8," + encodeURIComponent('<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="3" fill="#0072C6" /><text x="4" y="17" fill="white" font-size="13" font-weight="bold" font-family="Arial">O</text></svg>');

const providerTheme = {
    google: {
        connectedBg: '#f0fdf4',
        connectedBorder: '#86efac',
        connectedText: '#16a34a',
        notConnectedBg: '#fafafa',
        notConnectedBorder: '#e2e8f0',
        notConnectedText: '#64748b',
        hoverBg: '#dcfce7',
        dotColor: '#22c55e',
    },
    outlook: {
        connectedBg: '#eff6ff',
        connectedBorder: '#93c5fd',
        connectedText: '#1d4ed8',
        notConnectedBg: '#fafafa',
        notConnectedBorder: '#e2e8f0',
        notConnectedText: '#64748b',
        hoverBg: '#dbeafe',
        dotColor: '#3b82f6',
    },
};

// Pulse keyframe injected once
const pulseStyle = `
@keyframes statusPulse {
    0%   { transform: scale(1); opacity: 1; }
    50%  { transform: scale(1.6); opacity: 0.35; }
    100% { transform: scale(1); opacity: 1; }
}`;
if (typeof document !== 'undefined' && !document.getElementById('cal-badge-pulse')) {
    const s = document.createElement('style');
    s.id = 'cal-badge-pulse';
    s.innerHTML = pulseStyle;
    document.head.appendChild(s);
}

const ProviderBadge = ({ provider, isConnected, email, lastSyncedAt, onClick }) => {
    const icon = provider === 'google' ? GOOGLE_ICON : OUTLOOK_ICON;
    const label = provider === 'google' ? 'Google Calendar' : 'Outlook Calendar';
    const theme = providerTheme[provider];

    const tooltipTitle = isConnected
        ? (
            <Box sx={{ p: 0.5 }}>
                <Typography variant="caption" fontWeight={700} display="block">{label}</Typography>
                {email && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.85 }}>
                        {email}
                    </Typography>
                )}
                {lastSyncedAt && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.7, mt: 0.3 }}>
                        Last synced: {new Date(lastSyncedAt).toLocaleTimeString()}
                    </Typography>
                )}
                <Typography variant="caption" display="block" sx={{ color: '#86efac', mt: 0.5, fontWeight: 600 }}>
                    ✓ Connected & Syncing
                </Typography>
            </Box>
        )
        : (
            <Box sx={{ p: 0.5 }}>
                <Typography variant="caption" fontWeight={700} display="block">{label}</Typography>
                <Typography variant="caption" display="block" sx={{ opacity: 0.75, mt: 0.3 }}>
                    Not connected. Click to connect.
                </Typography>
            </Box>
        );

    return (
        <Tooltip title={tooltipTitle} arrow placement="bottom" componentsProps={{
            tooltip: { sx: { bgcolor: '#1e293b', maxWidth: 220, borderRadius: 2, p: 1 } },
            arrow: { sx: { color: '#1e293b' } }
        }}>
            <Box
                onClick={onClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                    cursor: onClick ? 'pointer' : 'default',
                    px: 1.4,
                    py: 0.55,
                    borderRadius: 2,
                    border: '1.5px solid',
                    borderColor: isConnected ? theme.connectedBorder : theme.notConnectedBorder,
                    bgcolor: isConnected ? theme.connectedBg : theme.notConnectedBg,
                    transition: 'all 0.22s ease',
                    userSelect: 'none',
                    '&:hover': onClick ? {
                        bgcolor: theme.hoverBg,
                        borderColor: isConnected ? theme.dotColor : '#94a3b8',
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${theme.dotColor}22`,
                    } : {},
                }}
            >
                {/* Pulsing dot */}
                <Box sx={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
                    <Box sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        bgcolor: isConnected ? theme.dotColor : '#cbd5e1',
                    }} />
                    {isConnected && (
                        <Box sx={{
                            position: 'absolute', top: 0, left: 0,
                            width: 8, height: 8, borderRadius: '50%',
                            bgcolor: theme.dotColor,
                            animation: 'statusPulse 2s ease-in-out infinite',
                        }} />
                    )}
                </Box>

                <img src={icon} width="13" alt={provider} style={{ opacity: isConnected ? 1 : 0.4, flexShrink: 0 }} />

                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        color: isConnected ? theme.connectedText : theme.notConnectedText,
                        letterSpacing: '0.2px',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {provider === 'google' ? 'Google' : 'Outlook'}
                    {' '}
                    {isConnected ? '· Synced' : '· Connect'}
                </Typography>

                {isConnected
                    ? <CheckCircleIcon sx={{ fontSize: 12, color: theme.connectedText, ml: 0.3 }} />
                    : <AddCircleOutlineIcon sx={{ fontSize: 12, color: '#94a3b8', ml: 0.3 }} />
                }
            </Box>
        </Tooltip>
    );
};

/**
 * CalendarStatusBadge
 * @param {object} google   - { isConnected, email, lastSyncedAt }
 * @param {object} outlook  - { isConnected, email, lastSyncedAt }
 * @param {boolean} loading
 * @param {function} onConnectGoogle
 * @param {function} onConnectOutlook
 */
const CalendarStatusBadge = ({
    google = {},
    outlook = {},
    loading = false,
    onConnectGoogle,
    onConnectOutlook,
}) => {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="rounded" width={120} height={30} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rounded" width={120} height={30} sx={{ borderRadius: 2 }} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ProviderBadge
                provider="google"
                isConnected={google.isConnected}
                email={google.email}
                lastSyncedAt={google.lastSyncedAt}
                onClick={!google.isConnected ? onConnectGoogle : undefined}
            />
            <ProviderBadge
                provider="outlook"
                isConnected={outlook.isConnected}
                email={outlook.email}
                lastSyncedAt={outlook.lastSyncedAt}
                onClick={!outlook.isConnected ? onConnectOutlook : undefined}
            />
        </Box>
    );
};

export default CalendarStatusBadge;
